import { RouterLink } from '@/common/components/router-links/RouterLink';
import { Trans } from '@lingui/react/macro';
import { MailCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { useAppForm } from '@/lib/form';
import { appFormOptions } from '@/common/form/appFormOptions';
import { requiredInput } from '@/common/form/requiredInput';
import { FormErrors } from '@/common/components/form-errors/FormErrors';
import { FormSubmit } from '@/common/components/form-submit/FormSubmit';
import { AccountService } from '@/features/auth/services/AccountService';

const RESEND_COOLDOWN_SECONDS = 60;

const resendEmailSchema = z.object({
  email: requiredInput(z.email()),
});

interface VerifyEmailPageProps {
  email?: string;
}

export const VerifyEmailPage = ({ email }: VerifyEmailPageProps) => {
  const [cooldown, setCooldown] = useState(0);
  const [showEmailForm, setShowEmailForm] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const resendMutation = useMutation({
    mutationFn: (emailToSend: string) =>
      AccountService.resendVerificationEmail({ email: emailToSend }),
    onSuccess: () => {
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setShowEmailForm(false);
    },
  });

  const form = useAppForm(
    appFormOptions({
      schema: resendEmailSchema,
      defaultValues: { email: null },
      onSubmit: async ({ value }) => {
        await resendMutation.mutateAsync(value.email);
      },
    })
  );

  return (
    <div className="flex flex-col items-center gap-2 text-center">
      <div className="mt-4 flex size-16 items-center justify-center rounded-full bg-blue-50">
        <MailCheck size={32} className="text-blue-600" />
      </div>

      <h1 className="mt-2 text-2xl font-bold tracking-tight text-gray-900">
        <Trans>Check your email</Trans>
      </h1>
      <p className="mt-1 max-w-sm text-sm text-gray-500">
        <Trans>
          We sent a verification link to your email address. Please click the link to activate your
          account.
        </Trans>
      </p>

      <div className="mt-6 w-full">
        {resendMutation.isSuccess && (
          <p className="mb-3 text-sm font-medium text-green-600">
            <Trans>Verification email sent!</Trans>
          </p>
        )}

        {email && !showEmailForm ? (
          <>
            {resendMutation.isError && (
              <p className="mb-3 text-sm font-medium text-red-600">
                <Trans>Failed to resend email. Please try again.</Trans>
              </p>
            )}
            <button
              type="button"
              disabled={cooldown > 0 || resendMutation.isPending}
              onClick={() => resendMutation.mutate(email)}
              className="text-sm font-semibold text-blue-600 transition-colors hover:text-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {cooldown > 0 ? (
                <Trans>Resend email in {cooldown}s</Trans>
              ) : (
                <Trans>Resend verification email</Trans>
              )}
            </button>
          </>
        ) : showEmailForm ? (
          <form.AppForm>
            <form.FormElement>
              <FormErrors className="mb-4" />
              <form.AppField name="email">
                {(field) => (
                  <field.TextField
                    label={<Trans>Email</Trans>}
                    type="email"
                    placeholder="name@company.com"
                  />
                )}
              </form.AppField>
              <FormSubmit className="mt-4 w-full" disabled={cooldown > 0}>
                {cooldown > 0 ? (
                  <Trans>Resend email in {cooldown}s</Trans>
                ) : (
                  <Trans>Resend verification email</Trans>
                )}
              </FormSubmit>
            </form.FormElement>
          </form.AppForm>
        ) : (
          <button
            type="button"
            onClick={() => setShowEmailForm(true)}
            className="text-sm font-semibold text-blue-600 transition-colors hover:text-blue-500"
          >
            <Trans>Resend verification email</Trans>
          </button>
        )}
      </div>

      <p className="mt-8 text-sm text-gray-500">
        <Trans>Already verified?</Trans>{' '}
        <RouterLink to={'/login'}>
          <Trans>Sign in</Trans>
        </RouterLink>
      </p>
    </div>
  );
};
